import {
    Box,
    Button,
    Checkbox,
    FormControl,
    Icon,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material'
import { CloseOutlined } from '@mui/icons-material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { MRT_RowSelectionState } from 'material-react-table'
import { enqueueSnackbar } from 'notistack'
import {
    memo,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useDebounce from '../../CustomHooks/useDebounce'
import { useAlertTypeOptions } from '../../hooks/useAlertTypeOptions'

// Redux
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { setAlertsFilters } from '../../redux/slices/alertSlice'
import { setUserAlertPreferences } from '../../redux/slices/userSlice'

// Components
import { ColumnOrderListType } from '../../redux/slices/entitySlice'
import { updateLoader } from '../../redux/slices/loaderSlice'
import ColumnOrderList from '../Common/ColumnOrderList'
import DateRangeInput from '../Common/DateRangeInput'
import DynamicTable from '../Common/DynamicTable'
import EntityTableHeader from '../Common/EntityTableHeader'
import MultiSelectInput from '../Common/MultiSelectInput'
import SearchInAPIAndMultiSelectInput from '../Common/SearchInAPIAndMultiSelectInput'
import EntityListSearch from '../Entity/Components/EntityListSearch'

// Services & Utils
import {
    Applications,
    RoutePaths,
    UserPermissions,
} from '../../constants/constants'
import {
    addAlertNote,
    exportAlertsToExcel,
    fetchAlerts,
    updateAlertStatus,
    updateUserAlertPreferences,
} from '../../services/alertServices'
import {
    getDefaultAlertDateRange,
    isDefaultAlertDateRange,
} from '../../utils/alertDateDefaults'
import { buildAlertFilterParams } from '../../utils/buildAlertFilterParams'
import { fetchRecordsUIDsByTemplateId } from '../../services/entityInstanceServices'
import { fetchColumnsSelections } from '../../services/entityServices'
import { findTemplateByName } from '../../services/utils'
import theme from '../../theme/theme'
import DatetimeTypeData from '../Common/DatetimeTypeData'
import TruncatedText from '../Common/TruncatedText'
import ExportColumnsPopup from '../Entity/ExportColumnsPopup'

import {
    getSeverityColor,
    getStatusIconPath,
    getStatusTooltipText,
    stripTimeFromMessage,
} from '../../utils/AlertUtils'
import AlertsListCustomFilters from './AlertsListCustomFilters'

dayjs.extend(utc)
dayjs.extend(timezone)

// --- Constants & Styles ---

const tableTypography = {
    body: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.4,
    },
    emphasized: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#111827',
    },
    subtle: {
        fontWeight: 400,
        color: '#6b7280',
    },
}

const getDefaultColumnList = (
    t: (key: string) => string,
): ColumnOrderListType[] => [
    {
        id: 'message',
        name: t('Alert'),
        attributeName: 'message',
        columnName: t('Alert'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'shipmentName',
        name: t('Shipment Name'),
        attributeName: 'shipmentName',
        columnName: t('Shipment Name'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'purchaseOrder',
        name: t('Purchase Order'),
        attributeName: 'purchaseOrder',
        columnName: t('Purchase Order'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'containerNumber',
        name: t('Container Number'),
        attributeName: 'containerNumber',
        columnName: t('Container Number'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'sealine',
        name: t('Sealine'),
        attributeName: 'sealine',
        columnName: t('Sealine'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'vesselName',
        name: t('vessel'),
        attributeName: 'vesselName',
        columnName: t('vessel'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'severity',
        name: t('severity'),
        attributeName: 'severity',
        columnName: t('severity'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'time',
        name: t('Date'),
        attributeName: 'time',
        columnName: t('Date'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'status',
        name: t('Status'),
        attributeName: 'status',
        columnName: t('Status'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
    {
        id: 'note',
        name: t('Note'),
        attributeName: 'note',
        columnName: t('Note'),
        selected: true,
        columnSelection: true,
        attributeType: 'string',
        dataType: 'string',
        searchable: 'false',
    },
]

export interface IAlertRow {
    id: string
    alertId?: string
    message?: string
    severity?: string
    time?: string
    status?: number
    note?: string | null
    shipmentName?: string
    purchaseOrder?: string
    containerNumber?: string
    sealine?: string
    vesselName?: string
    shipmentUid?: string
    _raw?: any
}

interface AlertsListProps {
    templateId: string
}

const AlertsList = ({ templateId }: AlertsListProps): ReactElement => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const savedState = useAppSelector((state) => state.alerts)

    const org = useAppSelector((s) => s.org.org)
    const permissions = useAppSelector((state) => state.org.permissions)
    const userId = useAppSelector((s) => s.user.user?.id)
    const navList = useAppSelector((s) => s.nav.navList)
    const userLocalization = useAppSelector(
        (s) => s.user.user?.preferences?.localization,
    )
    const userTimeZone =
        userLocalization?.timeZone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

    // Permission Logic
    const canWrite = useMemo(() => {
        return (
            permissions.includes(UserPermissions.ORG_ADMIN) ||
            permissions.includes(UserPermissions.ENTITY_WRITER) ||
            permissions.includes(UserPermissions.DEVICE_WRITER) ||
            permissions.includes(UserPermissions.ENTITY_ANALYST) ||
            permissions.includes(UserPermissions.USER_ADMIN) ||
            permissions.includes(UserPermissions.USER_USERORGS)
        )
    }, [permissions])

    const fetchSize = 30
    const defaultColumns = useMemo(() => getDefaultColumnList(t), [t])

    const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
    const [editingNoteValue, setEditingNoteValue] = useState<string>('')

    // Filters
    const [severityFilter, setSeverityFilter] = useState<string[]>(
        savedState.severityFilter ?? [],
    )
    const [alertTypeFilter, setAlertTypeFilter] = useState<string[]>(
        savedState.alertTypeFilter ?? [],
    )
    const [readStatusFilter, setReadStatusFilter] = useState<string[]>(
        savedState.readStatusFilter ?? [],
    )
    const [productFilter, setProductFilter] = useState<any[]>(
        savedState.productFilter ?? [],
    )
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(
        savedState.dateRange ?? getDefaultAlertDateRange(),
    )
    const [searchValue, setSearchValue] = useState(savedState.searchValue ?? '')
    const [localSearchValue, setLocalSearchValue] = useState(
        savedState.searchValue ?? '',
    )
    const [selectedColumn, setSelectedColumn] = useState(
        savedState.selectedColumn ?? null,
    )
    const [sortParams, setSortParams] = useState(savedState.sortParams)

    const debouncedSeverityFilter = useDebounce(severityFilter, 1000)
    const debouncedAlertTypeFilter = useDebounce(alertTypeFilter, 1000)
    const debouncedReadStatusFilter = useDebounce(readStatusFilter, 1000)
    const debouncedProductFilter = useDebounce(productFilter, 1000)
    const debouncedDateRange = useDebounce(dateRange, 1000)
    const debouncedSearchValue = useDebounce(searchValue, 1000)
    const debouncedSelectedColumn = useDebounce(selectedColumn, 1000)
    const debouncedSortParams = useDebounce(sortParams, 1000)

    // Table UI State
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [lastClickedRowIndex, setLastClickedRowIndex] = useState<
        number | null
    >(null)
    const [searchableColumns, setSearchableColumns] = useState<any[]>([])
    const [columnFilters, setColumnFilters] = useState<
        { id: string; value: any }[]
    >(savedState.columnFilters ?? [])
    const debouncedColumnFilters = useDebounce(columnFilters, 1000)

    // Column Visibility (LocalStorage persistence)
    const [columnVisibilityList, setColumnVisibilityList] =
        useState<ColumnOrderListType[]>(defaultColumns)
    const [savedColumnVisibilityList, setSavedColumnVisibilityList] =
        useState<ColumnOrderListType[]>(defaultColumns)

    useEffect(() => {
        const key = `alerts_columnVisibility_${templateId}_v3`
        const savedColumns = localStorage.getItem(key)

        if (savedColumns) {
            try {
                const parsed: ColumnOrderListType[] = JSON.parse(savedColumns)
                // Merge any new default columns that don't exist in saved state
                const savedIds = new Set(parsed.map((c) => c.id))
                const newColumns = defaultColumns.filter(
                    (c) => !savedIds.has(c.id),
                )
                const merged =
                    newColumns.length > 0 ? [...parsed, ...newColumns] : parsed
                setColumnVisibilityList(merged)
                setSavedColumnVisibilityList(merged)
            } catch (error) {
                console.error('Failed to restore column visibility', error)
            }
        }
    }, [templateId, defaultColumns])

    const [openExportPopup, setOpenExportPopup] = useState(false)

    // Product Search API State
    const productTemplate = useMemo(
        () => findTemplateByName(navList, 'Product'),
        [navList],
    )
    const productTemplateId = productTemplate?.id
    const [productUidList, setProductUidList] = useState<any[]>([])
    const [productPage, setProductPage] = useState(1)
    const [productSearch, setProductSearch] = useState('')
    const [productHasMore, setProductHasMore] = useState(false)
    const [productFetching, setProductFetching] = useState(false)

    // Options for Static Dropdowns
    const SEVERITY_OPTIONS = useMemo(
        () => [
            { id: 'High Critical', name: t('High Critical') },
            { id: 'Low Critical', name: t('Low Critical') },
            { id: 'Critical', name: t('Critical') },

            { id: 'High Warning', name: t('High Warning') },
            { id: 'Low Warning', name: t('Low Warning') },
            { id: 'Warning', name: t('Warning') },

            { id: 'High Info', name: t('High Info') },
            { id: 'Low Info', name: t('Low Info') },
            { id: 'Info', name: t('Info') },

            { id: 'Normal', name: t('Normal') },
        ],
        [t],
    )

    const ALERT_TYPE_OPTIONS = useAlertTypeOptions()

    const READ_STATUS_OPTIONS = useMemo(
        () => [
            { id: 2, name: t('ignore') },
            { id: 1, name: t('read') },
            { id: 0, name: t('Unread') },
        ],
        [t],
    )

    const queryClient = useQueryClient()

    const [statusMenuAnchor, setStatusMenuAnchor] = useState<{
        el: HTMLElement
        row: IAlertRow
    } | null>(null)

    const statusMenuRowRef = useRef<IAlertRow | null>(null)

    const [headerMenuAnchor, setHeaderMenuAnchor] =
        useState<null | HTMLElement>(null)

    useEffect(() => {
        if (!userId || !org?.id) return

        const payload = {
            searchValue: debouncedSearchValue,
            selectedColumn: debouncedSelectedColumn,
            // Persist only a range the user actually picked. Saving the default would freeze it:
            // it would stop rolling forward, later be treated as an active filter, and pin the list
            // to a stale window. Storing [null, null] makes hydration resolve a fresh default.
            dateRange: isDefaultAlertDateRange(debouncedDateRange)
                ? ([null, null] as [string | null, string | null])
                : debouncedDateRange,
            severityFilter: debouncedSeverityFilter,
            alertTypeFilter: debouncedAlertTypeFilter,
            readStatusFilter: debouncedReadStatusFilter,
            productFilter: debouncedProductFilter,
            columnFilters: debouncedColumnFilters,
            sortParams: debouncedSortParams,
        }

        dispatch(setAlertsFilters(payload))
        dispatch(setUserAlertPreferences(payload))

        const syncPreferences = async () => {
            try {
                await updateUserAlertPreferences(userId, org.id, payload)
            } catch {
                enqueueSnackbar(t('Failed to save filter preferences'), {
                    variant: 'error',
                    autoHideDuration: 200,
                })
            }
        }

        syncPreferences()
    }, [
        t,
        debouncedSearchValue,
        debouncedSelectedColumn,
        debouncedDateRange,
        debouncedSeverityFilter,
        debouncedAlertTypeFilter,
        debouncedReadStatusFilter,
        debouncedProductFilter,
        debouncedColumnFilters,
        debouncedSortParams,
        dispatch,
        userId,
        org?.id,
    ])

    const handleSearchChange = useCallback((val: string) => {
        setLocalSearchValue(val)
        if (val === '') {
            setSearchValue('')
        }
    }, [])

    const handleTriggerSearch = useCallback(() => {
        setSearchValue(localSearchValue.trim())
    }, [localSearchValue])

    const hasRestoredColumn = useRef(!!savedState.selectedColumn)

    useEffect(() => {
        if (!templateId || !org?.id) return

        const getColumns = async () => {
            try {
                const apiColumns = await fetchColumnsSelections(
                    templateId,
                    org.id,
                )
                const filteredColumns = apiColumns
                    .filter((col: any) => col.searchable === true)
                    .map((col: any) => ({
                        attributeName: col.attributeName,
                        columnName: col.columnName || col.attributeName,
                        attributeType: col.attributeType,
                        dataType: col.dataType,
                    }))

                setSearchableColumns(filteredColumns)
                if (!hasRestoredColumn.current && filteredColumns.length > 0) {
                    setSelectedColumn(filteredColumns[0])
                    hasRestoredColumn.current = true
                }
            } catch (error) {
                console.error('Failed to load searchable columns', error)
            }
        }

        getColumns()
    }, [templateId, org?.id])

    useEffect(() => {
        if (!productTemplateId || !org?.id) return
        setProductFetching(true)

        const load = async () => {
            const query = {
                start: productPage,
                pageSize: 30,
                searchText: productSearch.trim(),
                filterBy: 'ACTIVE',
                includeUseOnly: true,
                includeCommonOrg: true,
                entitySearch: true,
            }

            try {
                const res = await fetchRecordsUIDsByTemplateId(
                    org.id,
                    productTemplateId,
                    query,
                )

                const updated =
                    res?.data?.map((i: any) => ({
                        ...i,
                        hasItemEdit: true,
                    })) || []

                setProductUidList((prev) =>
                    productPage === 1 ? updated : [...prev, ...updated],
                )

                setProductHasMore(updated.length === 30)
            } finally {
                setProductFetching(false)
            }
        }

        load()
    }, [productTemplateId, org?.id, productPage, productSearch])

    // Shared base params for count query, table query, and export
    const baseParams = useMemo(
        () =>
            buildAlertFilterParams(
                {
                    searchValue: debouncedSearchValue,
                    selectedColumn: debouncedSelectedColumn,
                    dateRange: debouncedDateRange,
                    severityFilter: debouncedSeverityFilter,
                    alertTypeFilter: debouncedAlertTypeFilter,
                    readStatusFilter: debouncedReadStatusFilter,
                    productFilter: debouncedProductFilter,
                    columnFilters: debouncedColumnFilters,
                    sortParams: debouncedSortParams,
                },
                userTimeZone,
            ),
        [
            debouncedSearchValue,
            debouncedSelectedColumn,
            debouncedDateRange,
            debouncedSeverityFilter,
            debouncedAlertTypeFilter,
            debouncedReadStatusFilter,
            debouncedProductFilter,
            debouncedColumnFilters,
            debouncedSortParams,
            userTimeZone,
        ],
    )

    const alertsQueryKey = useMemo(
        () => [
            'alerts-table-data',
            templateId,
            org?.id,
            userId,
            baseParams,
            debouncedColumnFilters,
        ],
        [templateId, org?.id, userId, baseParams, debouncedColumnFilters],
    )

    const {
        data,
        fetchNextPage,
        isFetching,
        isLoading,
        isError,
        refetch,
        hasNextPage,
    } = useInfiniteQuery({
        staleTime: 5000,
        queryKey: alertsQueryKey,
        queryFn: async ({ pageParam }: any) => {
            const queryParams = {
                ...baseParams,
                pageNo: pageParam + 1,
                pageSize: fetchSize,
            }

            const resp = await fetchAlerts(
                org?.id || '',
                templateId,
                queryParams,
            )

            return {
                alerts: resp?.alerts ?? [],
                totalRowCount: resp?.totalRowCount ?? 0,
            }
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const fetchedCount = lastPage.alerts?.length || 0
            if (fetchedCount < fetchSize) return undefined
            return allPages.length
        },
        refetchOnWindowFocus: false,
        enabled:
            !!userId &&
            (!!debouncedSelectedColumn || searchableColumns.length === 0),
    })

    const updateLocalRowStatus = useCallback(
        async (rawAlert: any, status: number) => {
            await queryClient.cancelQueries({ queryKey: alertsQueryKey })
            queryClient.setQueryData(alertsQueryKey, (oldData: any) => {
                if (!oldData) return oldData

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        alerts: page.alerts.map((alert: any) => {
                            const sameAlert =
                                alert.entityId === rawAlert.entityId &&
                                alert.timestamp === rawAlert.timestamp &&
                                alert.id === rawAlert.id &&
                                alert.ruleId === rawAlert.ruleId

                            if (sameAlert) {
                                return { ...alert, status }
                            }

                            return alert
                        }),
                    })),
                }
            })
        },
        [queryClient, alertsQueryKey],
    )

    const updateLocalRowNote = useCallback(
        (rawAlert: any, newNote: string) => {
            queryClient.setQueryData(alertsQueryKey, (oldData: any) => {
                if (!oldData) return oldData

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        alerts: page.alerts.map((alert: any) => {
                            const sameAlert =
                                alert.entityId === rawAlert.entityId &&
                                alert.timestamp === rawAlert.timestamp &&
                                alert.id === rawAlert.id &&
                                alert.ruleId === rawAlert.ruleId

                            if (sameAlert) {
                                return { ...alert, lastNote: newNote }
                            }

                            return alert
                        }),
                    })),
                }
            })
        },
        [queryClient, alertsQueryKey],
    )

    const flatData = useMemo(() => {
        const flatAlerts = data?.pages?.flatMap((page) => page.alerts) ?? []
        return flatAlerts.map((alert: any) => {
            const attrs = alert?.entityAttributes || {}
            return {
                id: `${alert.entityId}_${alert.timestamp}_${alert.id}_${alert.ruleId}`,
                alertId: alert?.ruleId ?? alert?.alertId,
                message: alert?.message,
                severity: alert?.severity,
                time: alert?.time,
                status: alert?.status,
                note: alert?.lastNote ?? alert?.note,
                shipmentName: attrs?.shipmentName ?? '',
                shipmentUid: attrs?.Id ?? attrs?.shipmentUid ?? '',
                purchaseOrder: attrs?.purchaseOrder ?? '',
                containerNumber: attrs?.containerNumber ?? '',
                sealine: attrs?.sealine ?? '',
                vesselName: attrs?.vesselName ?? '',
                _raw: alert,
            } as IAlertRow
        })
    }, [data])

    const totalDBRowCount = useMemo(
        () => data?.pages?.[0]?.totalRowCount ?? 0,
        [data],
    )
    const handleExport = useCallback(
        async (selectedColumns: string[]) => {
            if (!org?.id) return

            try {
                const queryParams = {
                    ...baseParams,
                    pageNo: 1,
                    pageSize: 10000,
                    entitySearch: false,
                    fromDate: dateRange?.[0] || '',
                    toDate: dateRange?.[1] || '',
                }

                // Apply column-level filters
                dispatch(updateLoader(true))

                // 2. Call Service
                const response = await exportAlertsToExcel(
                    org.id,
                    templateId,
                    queryParams,
                    selectedColumns,
                )
                dispatch(updateLoader(false))

                // 3. Handle File Download
                const url = globalThis.URL.createObjectURL(
                    new Blob([response.data]),
                )
                const link = document.createElement('a')
                link.href = url

                // Try to get filename from Content-Disposition header, fallback to default
                const contentDisposition =
                    response.headers['content-disposition']
                let fileName = `Alerts_Export_${dayjs().format('YYYYMMDD')}.xlsx`

                if (contentDisposition) {
                    const fileNameMatch =
                        contentDisposition.match(/filename="?([^"]+)"?/)
                    if (fileNameMatch?.[1]) {
                        fileName = fileNameMatch[1]
                    }
                }

                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()

                // Cleanup
                link.remove()
                globalThis.URL.revokeObjectURL(url)

                setOpenExportPopup(false) // Close popup
            } catch (error) {
                dispatch(updateLoader(false))
                console.error('Export failed', error)
            }
        },
        [org?.id, templateId, baseParams, dateRange, columnFilters, dispatch],
    )

    const handleShipmentNavigation = useCallback(
        (row: IAlertRow) => {
            const instanceId = row._raw?.entityId
            const targetTemplateId =
                row._raw?.entityAttributes?.templateId || templateId

            if (!instanceId || !org?.id) return

            navigate({
                pathname: RoutePaths.ccl,
                search: `?${new URLSearchParams({
                    applicationId: Applications.inTransit.id,
                    tabIndex: '1',
                    pageView: 'DETAILS_VIEW',
                    instanceId,
                    templateId: targetTemplateId,
                    orgId: org.id,
                }).toString()}`,
            })
        },
        [navigate, org?.id, templateId],
    )

    const updateStatus = useCallback(
        async (row: IAlertRow, newStatus: number) => {
            const alert = row._raw
            if (!alert || !org?.id) return

            updateLocalRowStatus(row._raw, newStatus)

            const payload = {
                entityId: alert.entityId,
                ruleId: alert.ruleId,
                id: alert.id,
                timestamp: alert.timestamp,
                status: newStatus,
            }

            try {
                await updateAlertStatus(org.id, payload)
                enqueueSnackbar(t('Status updated successfully'), {
                    variant: 'success',
                    autoHideDuration: 2000,
                })
                queryClient.invalidateQueries({
                    queryKey: ['alerts-unread-count-bell'],
                })
            } catch (_err) {
                enqueueSnackbar(t('Failed to update alert'), {
                    variant: 'error',
                })
                updateLocalRowStatus(row._raw, row.status ?? 0)
                console.log(_err)
            }
        },
        [org?.id, t, updateLocalRowStatus, queryClient],
    )

    const updateSelectedRowsStatus = useCallback(
        async (newStatus: number) => {
            const selectedIds = Object.keys(rowSelection).filter(
                (id) => rowSelection[id],
            )

            if (selectedIds.length === 0) {
                enqueueSnackbar(t('Please select one or more alerts'), {
                    variant: 'info',
                })
                return
            }

            const selectedRows = flatData.filter((row) => {
                return rowSelection[row.id]
            })

            if (selectedRows.length === 0) {
                // This should rarely happen if flatData and rowSelection are in sync
                enqueueSnackbar(t('No alerts found for selected rows'), {
                    variant: 'error',
                })
                return
            }

            // Optimistically update all selected rows in the local cache
            selectedRows.forEach((row) =>
                updateLocalRowStatus(row._raw, newStatus),
            )

            setRowSelection({}) // Clear selection after action

            const updatePromises = selectedRows.map((row) => {
                const alert = row._raw
                if (!alert || !org?.id) return Promise.resolve() // Skip if data is missing

                const payload = {
                    entityId: alert.entityId,
                    ruleId: alert.ruleId,
                    id: alert.id,
                    timestamp: alert.timestamp,
                    status: newStatus,
                }

                return updateAlertStatus(org.id, payload).catch((err) => {
                    console.error(`Failed to update alert ${row.id}:`, err)
                    enqueueSnackbar(t('Failed to update one or more alerts'), {
                        variant: 'error',
                    })
                    // Revert status for the failed one on API error
                    updateLocalRowStatus(row._raw, row.status ?? 0)
                })
            })

            await Promise.all(updatePromises)
            enqueueSnackbar(t('Selected alerts updated successfully'), {
                variant: 'success',
            })
            queryClient.invalidateQueries({
                queryKey: ['alerts-unread-count-bell'],
            })
        },
        [org?.id, t, updateLocalRowStatus, rowSelection, flatData, queryClient],
    )

    const columns = useMemo(() => {
        return [
            {
                id: 'customSelect',
                header: '‎',
                size: 20,
                enableColumnFilter: false,
                enableResizing: false,
                grow: false,
                enableSorting: false,
                Cell: ({ row }: any) => (
                    <Checkbox
                        size="small"
                        sx={{
                            padding: 0,
                            margin: 0,
                            marginLeft: '4px',
                        }}
                        checked={rowSelection[row.original.id] || false}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            const { checked } = e.target
                            const currentIndex = row.index

                            // Handle Shift + Click for range selection
                            if (
                                (e.nativeEvent as MouseEvent).shiftKey &&
                                lastClickedRowIndex !== null &&
                                flatData
                            ) {
                                const start = Math.min(
                                    lastClickedRowIndex,
                                    currentIndex,
                                )
                                const end = Math.max(
                                    lastClickedRowIndex,
                                    currentIndex,
                                )

                                const newSelection = { ...rowSelection }

                                // Select all rows in the range
                                for (let i = start; i <= end; i++) {
                                    const rowId = flatData[i]?.id
                                    if (rowId) {
                                        newSelection[rowId] = true
                                    }
                                }
                                setRowSelection(newSelection)
                            } else {
                                // Normal Click
                                setLastClickedRowIndex(currentIndex)
                                setRowSelection((prev) => ({
                                    ...prev,
                                    [row.original.id]: checked,
                                }))
                            }
                        }}
                    />
                ),
            },
            {
                header: t('Alert'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'message',
                id: 'message',
                size: 320,
                minSize: 130,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ cell, row }: any) => {
                    const cleanMessage = stripTimeFromMessage(cell.getValue())

                    const alertType = row.original._raw?.alertType
                    const alertTypeTitle = ALERT_TYPE_OPTIONS.find(
                        (e) => e.id === alertType,
                    )
                    return (
                        <TruncatedText
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                                minWidth: 0,
                            }}
                        >
                            <TruncatedText
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#000000',
                                    letterSpacing: '0.4px',
                                }}
                            >
                                {alertTypeTitle?.name || t('Unknown Alert')}
                            </TruncatedText>

                            {/* MESSAGE */}
                            <TruncatedText text={cleanMessage} />
                        </TruncatedText>
                    )
                },
            },
            {
                header: t('Shipment Name'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'shipmentName',
                id: 'shipmentName',
                size: 180,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ cell, row }: any) => (
                    <TruncatedText
                        sx={{
                            ...tableTypography.emphasized,
                            cursor: 'pointer',
                            color: theme.palette.primary.main,
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleShipmentNavigation(row.original)
                        }}
                    >
                        {cell.getValue()}
                    </TruncatedText>
                ),
            },
            {
                header: t('Purchase Order'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'purchaseOrder',
                id: 'purchaseOrder',
                size: 100,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ cell }: any) => (
                    <TruncatedText
                        sx={{
                            ...tableTypography.body,
                        }}
                    >
                        {cell.getValue()}
                    </TruncatedText>
                ),
            },
            {
                header: t('Container Number'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'containerNumber',
                id: 'containerNumber',
                size: 160,
                enableSorting: true,
                enableColumnFilter: true,
                Filter: AlertsListCustomFilters,
                Cell: ({ cell }: any) => (
                    <TruncatedText
                        sx={{
                            ...tableTypography.body,
                        }}
                    >
                        {cell.getValue()}
                    </TruncatedText>
                ),
            },
            {
                header: t('Sealine'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'sealine',
                id: 'sealine',
                size: 120,
                enableSorting: true,
                enableColumnFilter: true,
                Filter: AlertsListCustomFilters,
                Cell: ({ cell }: any) => (
                    <TruncatedText
                        sx={{
                            ...tableTypography.body,
                        }}
                    >
                        {cell.getValue()}
                    </TruncatedText>
                ),
            },
            {
                header: t('vessel'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'vesselName',
                id: 'vesselName',
                size: 160,
                enableSorting: true,
                enableColumnFilter: true,
                Filter: AlertsListCustomFilters,
                Cell: ({ cell }: any) => (
                    <TruncatedText
                        sx={{
                            ...tableTypography.body,
                        }}
                    >
                        {cell.getValue()}
                    </TruncatedText>
                ),
            },
            {
                header: t('severity'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'severity',
                id: 'severity',
                size: 80,
                enableSorting: true,
                enableColumnFilter: false,
                Cell: ({ row, cell }: any) => {
                    const value = cell.getValue()
                    const status = row.original?.status
                    const isReadOrIgnore = status === 1 || status === 2
                    return (
                        <TruncatedText
                            sx={{
                                ...tableTypography.body,
                                fontWeight: isReadOrIgnore ? 400 : 600,
                                color: getSeverityColor(value),
                            }}
                        >
                            {value}
                        </TruncatedText>
                    )
                },
            },
            {
                header: t('Date'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'time',
                id: 'time',
                size: 180,
                enableSorting: true,
                enableColumnFilter: false,
                Cell: ({ row }: any) => {
                    return (
                        <DatetimeTypeData
                            dataValue={{
                                datetime: row.original?.time,
                                timezone: userTimeZone,
                            }}
                            showTimezone={false}
                            fontSize="14px"
                        />
                    )
                },
            },
            {
                header: t('status'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'status',
                id: 'status',
                size: 80,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }: any) => {
                    const statusValue = row.original.status
                    const severity = row.original?.severity
                    const iconPath = getStatusIconPath(statusValue, severity)
                    const tooltipText = getStatusTooltipText(
                        statusValue,
                        severity,
                        t,
                    )

                    const getToggleStatus = () => (statusValue === 0 ? 1 : 0)

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Tooltip
                                title={
                                    canWrite
                                        ? `${tooltipText}`
                                        : t('noPermissionToUpdateStatus')
                                }
                                arrow
                                placement="top"
                            >
                                <Box
                                    component="img"
                                    src={iconPath}
                                    alt={tooltipText}
                                    sx={{
                                        width: '32px',
                                        height: '32px',
                                        display: 'block',
                                        cursor: canWrite
                                            ? 'pointer'
                                            : 'default',
                                        opacity: canWrite ? 1 : 0.6,
                                    }}
                                    onClick={(e) => {
                                        if (!canWrite) return
                                        e.stopPropagation()
                                        updateStatus(
                                            row.original,
                                            getToggleStatus(),
                                        )
                                    }}
                                />
                            </Tooltip>
                            {canWrite && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        statusMenuRowRef.current = row.original
                                        setStatusMenuAnchor({
                                            el: e.currentTarget,
                                            row: row.original,
                                        })
                                    }}
                                    sx={{ padding: 0 }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        height={18}
                                        width={18}
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M12.7071 14.7071C12.3166 15.0976 11.6834 15.0976 11.2929 14.7071L6.29289 9.70711C5.90237 9.31658 5.90237 8.68342 6.29289 8.29289C6.68342 7.90237 7.31658 7.90237 7.70711 8.29289L12 12.5858L16.2929 8.29289C16.6834 7.90237 17.3166 7.90237 17.7071 8.29289C18.0976 8.68342 18.0976 9.31658 17.7071 9.70711L12.7071 14.7071Z"
                                            fill="#808080"
                                        />
                                    </svg>
                                </IconButton>
                            )}
                        </Box>
                    )
                },
            },
            {
                header: t('note'),
                Header: (d: any) => (
                    <EntityTableHeader text={d.column.columnDef.header} />
                ),
                accessorKey: 'note',
                id: 'note',
                enableSorting: false,
                enableColumnFilter: false,
                minSize: 220,

                Cell: ({ cell, row }: any) => {
                    const id = row.original.id
                    const isEditing = editingNoteId === id
                    const originalNote = cell.getValue() || ''

                    const isNoteEmpty = !editingNoteValue.trim()

                    // Enable edit mode on click
                    const handleStartEdit = () => {
                        if (!isEditing && canWrite) {
                            setEditingNoteId(id)
                            setEditingNoteValue(originalNote)
                        }
                    }

                    const handleCancel = (e: React.MouseEvent) => {
                        e.stopPropagation()
                        setEditingNoteId(null)
                        setEditingNoteValue('')
                    }

                    const handleSave = async (e: React.MouseEvent) => {
                        e.stopPropagation()

                        const alert = row.original._raw

                        if (!alert || !org?.id) return

                        const payload = {
                            entityId: alert.entityId,
                            ruleId: alert.ruleId,
                            alertId: alert.id,
                            alertTimestamp: alert.timestamp,
                            noteText: editingNoteValue,
                        }

                        try {
                            // Optimistically update the UI
                            updateLocalRowNote(alert, editingNoteValue)

                            // Send request to backend
                            await addAlertNote(org.id, payload)

                            enqueueSnackbar(t('Note saved successfully'), {
                                variant: 'success',
                            })
                        } catch (err) {
                            console.error('Failed to save note:', err)

                            enqueueSnackbar(t('Failed to save note'), {
                                variant: 'error',
                            })

                            // Revert on failure
                            updateLocalRowNote(alert, row.original.note ?? '')
                        }

                        setEditingNoteId(null)
                        setEditingNoteValue('')
                    }

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'flex-start', // Keeps icons at top if textarea grows
                                gap: 1,
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent row click navigation
                        >
                            <textarea
                                readOnly={!canWrite}
                                style={{
                                    ...tableTypography.subtle,
                                    width: '100%',
                                    resize: 'vertical', // Keeps the resize capability
                                    minHeight: '30px',
                                    padding: '6px',
                                    borderRadius: '4px',
                                    border: isEditing
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : `1px solid ${theme.palette.primary.main}`,
                                    outline: 'none',
                                    backgroundColor: canWrite
                                        ? '#ffffff'
                                        : '#f5f5f5',
                                    transition: 'all 0.2s ease',
                                    cursor: canWrite ? 'text' : 'default',
                                    fontFamily:
                                        '-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
                                }}
                                value={
                                    isEditing ? editingNoteValue : originalNote
                                }
                                onChange={(e) =>
                                    setEditingNoteValue(e.target.value)
                                }
                                onClick={handleStartEdit}
                                onKeyDown={(e) => {
                                    const navKeys = [
                                        'ArrowLeft',
                                        'ArrowRight',
                                        'ArrowUp',
                                        'ArrowDown',
                                        'Enter',
                                    ]

                                    if (navKeys.includes(e.key)) {
                                        e.stopPropagation()
                                    }
                                }}
                                placeholder={
                                    canWrite
                                        ? isEditing
                                            ? ''
                                            : t('addANote') + '...'
                                        : ''
                                }
                            />

                            {/* Show Icons ONLY when editing AND field is not empty */}
                            {isEditing && canWrite && !isNoteEmpty && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 1,
                                        paddingTop: '4px',
                                    }}
                                >
                                    <Tooltip title={t('save')}>
                                        <Box
                                            component="img"
                                            src="/assets/images/icons/tick.svg"
                                            alt="Save"
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                cursor: 'pointer',
                                                '&:hover': { opacity: 0.8 },
                                                border: '1px solid #C4C4C4',
                                                borderRadius: '2px',
                                                padding: '5px',
                                            }}
                                            onClick={handleSave}
                                        />
                                    </Tooltip>

                                    <Tooltip title={t('cancel')}>
                                        <Box
                                            component="img"
                                            src="/assets/images/icons/cancel.svg"
                                            alt="Cancel"
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                cursor: 'pointer',
                                                '&:hover': { opacity: 0.8 },
                                                border: '1px solid #C4C4C4',
                                                borderRadius: '2px',
                                                padding: '7px',
                                            }}
                                            onClick={handleCancel}
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    )
                },
            },
        ]
    }, [
        t,
        userTimeZone,
        handleShipmentNavigation,
        rowSelection,
        editingNoteId,
        editingNoteValue,
        flatData,
        lastClickedRowIndex,
        updateLocalRowNote,
        updateStatus,
        canWrite,
        ALERT_TYPE_OPTIONS,
        org?.id,
    ])

    // Column Management logic
    const columnVisibility = useMemo(() => {
        const obj: Record<string, boolean> = {}
        columnVisibilityList.forEach((i) => (obj[i.id] = i.selected))
        return obj
    }, [columnVisibilityList])

    const columnOrder = useMemo(() => {
        return ['customSelect', ...columnVisibilityList.map((c) => c.id)]
    }, [columnVisibilityList])

    const onSaveColumnVisibility = () => {
        localStorage.setItem(
            `alerts_columnVisibility_${templateId}_v3`,
            JSON.stringify(columnVisibilityList),
        )
        enqueueSnackbar(t('displayColumnsSavedSuccess'), { variant: 'success' })
    }

    const onResetColumnVisibility = () => {
        setColumnVisibilityList(savedColumnVisibilityList)
        enqueueSnackbar(t('displayColumnsResetSuccess'), { variant: 'success' })
    }

    const filterBoxStyle = {
        background: 'white',
        borderRadius: '5px',
        flexShrink: 0,
    }

    const filtersCount = useMemo(() => {
        let count = 0
        if (searchValue) count++
        if (severityFilter.length > 0) count++
        if (alertTypeFilter.length > 0) count++
        if (readStatusFilter.length > 0) count++
        if (productFilter.length > 0) count++
        // The pre-selected default window is not a user-applied filter — counting it would keep the
        // badge at 1 permanently and make "Clear All Filters" look broken.
        if (
            (dateRange[0] || dateRange[1]) &&
            !isDefaultAlertDateRange(dateRange)
        )
            count++
        if (columnFilters.length > 0)
            count += columnFilters.filter((cf) => cf.value).length
        return count
    }, [
        searchValue,
        severityFilter,
        alertTypeFilter,
        readStatusFilter,
        productFilter,
        dateRange,
        columnFilters,
    ])

    const hasFilters = filtersCount > 0

    const clearAllFilters = useCallback(() => {
        setSearchValue('')
        setLocalSearchValue('')
        setSeverityFilter([])
        setAlertTypeFilter([])
        setReadStatusFilter([])
        setProductFilter([])
        // Back to the default last-60-days window rather than an empty range: with no dates the
        // backend applies the same 60-day window anyway, so showing it keeps the picker honest about
        // what the list is actually displaying.
        setDateRange(getDefaultAlertDateRange())
        setColumnFilters([])
    }, [])

    return (
        <>
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* HEADER */}
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                        background: theme.tableHeader.color,
                        padding: '0px 20px',
                        gap: '20px',
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        gap="15px"
                        sx={{
                            padding: '15px 0px',
                            flexWrap: 'nowrap',
                            minWidth: 0,
                            overflowX: 'auto',
                        }}
                    >
                        <EntityListSearch
                            onSearchChange={handleSearchChange}
                            searchValue={localSearchValue}
                            triggerSearch={handleTriggerSearch}
                            onColumnSelect={(col) => setSelectedColumn(col)}
                            searchableColumns={searchableColumns}
                            selectedColumn={selectedColumn}
                        />

                        <Box sx={{ ...filterBoxStyle, width: '130px' }}>
                            <FormControl fullWidth size="small">
                                <MultiSelectInput
                                    id="alert-type-select"
                                    label={t('Alert Type')}
                                    isLegend={true}
                                    ui_width="260px"
                                    value={alertTypeFilter}
                                    onChange={(_id: string, val: string[]) =>
                                        setAlertTypeFilter(val)
                                    }
                                    list={ALERT_TYPE_OPTIONS}
                                    displayInputLabel={false}
                                    renderLabelInValue={true}
                                    displayEmpty={true}
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ ...filterBoxStyle, width: '115px' }}>
                            <FormControl fullWidth size="small">
                                <MultiSelectInput
                                    id="severity-select"
                                    label={t('severity')}
                                    isLegend={true}
                                    ui_width="160px"
                                    value={severityFilter}
                                    onChange={(_id: string, val: string[]) =>
                                        setSeverityFilter(val)
                                    }
                                    list={SEVERITY_OPTIONS}
                                    displayInputLabel={false}
                                    renderLabelInValue={true}
                                    displayEmpty={true}
                                />
                            </FormControl>
                        </Box>

                        {/* Product Filter using SearchInAPIAndMultiSelectInput */}
                        <Box sx={{ ...filterBoxStyle, width: '115px' }}>
                            <FormControl fullWidth size="small">
                                <SearchInAPIAndMultiSelectInput
                                    showSelectedCount={true}
                                    id="product-filter"
                                    label={t('Product')}
                                    useRawLabel={true}
                                    isLegend={true}
                                    ui_width="260px"
                                    showLabel={false}
                                    placeholder={t('Product')}
                                    value={productFilter}
                                    onSelect={(
                                        id: string,
                                        val: any[] | null,
                                    ) => {
                                        setProductFilter(val ?? [])
                                    }}
                                    list={productUidList}
                                    hasAddNew={false}
                                    onSearch={(id: any, text: any) => {
                                        setProductSearch(text)
                                        setProductPage(1)
                                    }}
                                    onFetchNextPage={() => {
                                        if (
                                            !productFetching &&
                                            productHasMore
                                        ) {
                                            setProductPage((p) => p + 1)
                                        }
                                    }}
                                    hasMore={productHasMore}
                                    isFetching={productFetching}
                                    itemId="entityId"
                                    itemName="uid"
                                    sx={{
                                        '& #product-filter-input-label': {
                                            top: '-1px !important',
                                            lineHeight: '0.71 !important',
                                            'padding-top': '0px !important',
                                        },

                                        '& #product-filter-select': {
                                            padding: '0px !important',
                                            height: '15px !important',
                                        },

                                        '& .MuiOutlinedInput-root': {
                                            padding: '12px 8px 12px 14px',
                                            paddingRight: '48px',
                                            alignItems: 'center',
                                            height: '37px',
                                        },
                                    }}
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ width: '130px', ...filterBoxStyle }}>
                            <FormControl fullWidth size="small">
                                <MultiSelectInput
                                    id="status-select"
                                    label={t('Alert Status')}
                                    ui_width="130px"
                                    isLegend={true}
                                    value={readStatusFilter}
                                    onChange={(_id: string, val: string[]) =>
                                        setReadStatusFilter(val)
                                    }
                                    list={READ_STATUS_OPTIONS}
                                    displayInputLabel={false}
                                    renderLabelInValue={true}
                                    displayEmpty={true}
                                />
                            </FormControl>
                        </Box>

                        <Box sx={{ minWidth: '263px' }}>
                            <DateRangeInput
                                id="alerts-date-range"
                                label=""
                                placeholderLabel={t('Filter Alerts By Date')}
                                value={dateRange}
                                onChange={(_: any, value: any) =>
                                    setDateRange(value)
                                }
                                clearable
                                minDate={dayjs().subtract(364 * 5, 'day')}
                                maxDate={dayjs()}
                            />
                        </Box>

                        {hasFilters ? (
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: '#00000042',
                                        color: '#fff',
                                        padding: '8px 15px 8px 12px',
                                        borderRadius: '25px',
                                        gap: '6px',
                                        cursor: 'pointer',
                                        minWidth: '158px',
                                        '&:hover': {
                                            background: '#00000048',
                                        },
                                    }}
                                    onClick={clearAllFilters}
                                >
                                    <Box>
                                        <Icon
                                            fontSize="small"
                                            id="clear-filters-button"
                                            aria-label={t('clearFilters')}
                                            title={t('clearFilters')}
                                        >
                                            <CloseOutlined
                                                sx={{
                                                    color: '#fff',
                                                    marginTop: '1px',
                                                }}
                                            />
                                        </Icon>
                                    </Box>
                                    <Box>
                                        <Typography>
                                            {t('clearFilters')}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            borderRadius: '25px',
                                            background: '#fff',
                                            color: '#000',
                                            marginLeft: '5px',
                                        }}
                                    >
                                        <Typography>{filtersCount}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}
                    </Box>

                    {/* Right Side: Counter + Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 'auto',
                            gap: '15px',
                            flexWrap: 'nowrap',
                            padding: '15px 0px',
                            flexShrink: 0,
                        }}
                    >
                        <Tooltip title={t('refresh')}>
                            <IconButton
                                onClick={() => {
                                    refetch()
                                    queryClient.invalidateQueries({
                                        queryKey: ['alerts-unread-count-bell'],
                                    })
                                }}
                            >
                                <img
                                    height={20}
                                    src="/assets/images/icons/refresh.svg"
                                    alt="Refresh"
                                />
                            </IconButton>
                        </Tooltip>

                        <Tooltip
                            title={
                                canWrite
                                    ? t('markSelectedAsRead/Status')
                                    : t('permissionDenied')
                            }
                        >
                            <span>
                                <IconButton
                                    disabled={!canWrite}
                                    onClick={(e) =>
                                        setHeaderMenuAnchor(e.currentTarget)
                                    }
                                >
                                    <img
                                        height={20}
                                        src="/assets/images/icons/mark-as-read.svg"
                                        alt="Mark as Read/Status"
                                        style={{
                                            filter: canWrite
                                                ? 'none'
                                                : 'grayscale(1)',
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <ColumnOrderList
                            list={columnVisibilityList}
                            handleUpdateList={(newList) =>
                                setColumnVisibilityList(newList)
                            }
                            handleOnSaveList={(newList) => {
                                setColumnVisibilityList(newList)
                                onSaveColumnVisibility()
                            }}
                            handleOnResetList={() => {
                                setColumnVisibilityList(
                                    savedColumnVisibilityList,
                                )
                                onResetColumnVisibility()
                            }}
                            isRightSide={false}
                        />

                        <Button
                            onClick={() => setOpenExportPopup(true)}
                            sx={{
                                minWidth: '25px',
                                maxWidth: '30px',
                                marginLeft: '5px',
                            }}
                            id="export-entities-button"
                        >
                            <Tooltip title={t('exportAsXls')} placement="top">
                                <img
                                    src="/assets/images/icons/file_save.svg"
                                    alt="Export"
                                />
                            </Tooltip>
                        </Button>
                    </Box>
                </Box>

                {/* TABLE */}
                <Box
                    sx={{ flex: '1 1 auto', overflow: 'hidden', minHeight: 0 }}
                >
                    <DynamicTable
                        tableIdentifier={`alerts-${templateId}`}
                        columns={columns}
                        data={flatData}
                        layoutMode="grid"
                        totalDBRowCount={totalDBRowCount}
                        selectionColumnId="customSelect"
                        fetchNextPage={fetchNextPage as any}
                        isError={!!isError}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        setRowSelection={setRowSelection}
                        hasNextPage={hasNextPage}
                        sortParams={sortParams}
                        setSortParams={setSortParams}
                        rowHeight={50}
                        rowSelection={rowSelection}
                        hasRowSelection={false}
                        columnVisibility={columnVisibility}
                        columnOrder={columnOrder}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        height={'100%'}
                        getRowBackground={(row: IAlertRow) => {
                            if (row.status === 1 || row.status === 2) {
                                return '#eeeeee' // grey
                            }
                            return '#ffffff' // normal
                        }}
                    />
                </Box>
            </Box>
            <Box>
                <Menu
                    anchorEl={statusMenuAnchor?.el}
                    open={Boolean(statusMenuAnchor)}
                    onClose={() => setStatusMenuAnchor(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    {statusMenuRowRef.current?.status !== 1 && (
                        <MenuItem
                            onClick={() => {
                                if (statusMenuAnchor?.row)
                                    updateStatus(statusMenuAnchor.row, 1)
                                setStatusMenuAnchor(null)
                            }}
                        >
                            {t('Mark as Read')}
                        </MenuItem>
                    )}

                    {statusMenuRowRef.current?.status !== 0 && (
                        <MenuItem
                            onClick={() => {
                                if (statusMenuAnchor?.row)
                                    updateStatus(statusMenuAnchor.row, 0)
                                setStatusMenuAnchor(null)
                            }}
                        >
                            {t('Mark as Unread')}
                        </MenuItem>
                    )}

                    {statusMenuRowRef.current?.status !== 2 && (
                        <MenuItem
                            onClick={() => {
                                if (statusMenuAnchor?.row)
                                    updateStatus(statusMenuAnchor.row, 2)
                                setStatusMenuAnchor(null)
                            }}
                        >
                            {t('Mark as Ignored')}
                        </MenuItem>
                    )}
                </Menu>

                <Menu
                    anchorEl={headerMenuAnchor}
                    open={Boolean(headerMenuAnchor)}
                    onClose={() => setHeaderMenuAnchor(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            updateSelectedRowsStatus(1) // Status 1: Read
                            setHeaderMenuAnchor(null)
                        }}
                    >
                        {t('Mark Selected as Read')}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            updateSelectedRowsStatus(0) // Status 0: Unread
                            setHeaderMenuAnchor(null)
                        }}
                    >
                        {t('Mark Selected as Unread')}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            updateSelectedRowsStatus(2) // Status 2: Ignored
                            setHeaderMenuAnchor(null)
                        }}
                    >
                        {t('Mark Selected as Ignored')}
                    </MenuItem>
                </Menu>
            </Box>
            {openExportPopup && (
                <ExportColumnsPopup
                    open={openExportPopup}
                    onClose={() => setOpenExportPopup(false)}
                    columns={columnVisibilityList}
                    onExport={handleExport}
                />
            )}
        </>
    )
}

export default memo(AlertsList)
